<script>
    // Import Swiper Svelte components
    import { Navigation, Pagination, Scrollbar, A11y } from "swiper";
    import { Swiper, SwiperSlide } from "swiper/svelte/swiper-svelte.js";
    import {
        searchFilter,
        positionFilter,
        getTeams,
        filterByTeam,
        gameStrings,
    } from "./filter_utils.js";
    import GameCard from "./GameCard.svelte";
    import Lineup from "./Lineup.svelte";
    import PlayerCard from "./PlayerCard.svelte";
    import PlayerModal from "./PlayerModal.svelte";
    import PremiumOnly from "./PremiumOnly.svelte";
    import "swiper/swiper-bundle.min.css";
    // swiper core styles
    import "swiper/swiper.min.css";
    import { onMount } from "svelte";
    // modules styles
    import "swiper/modules/navigation/navigation.min.css";
    import "swiper/modules/pagination/pagination.min.css";
    let pos_filter = "all";

    let projFP = 0;
    let strategy = "projection";
    let strategy_impact = 1;
    let exposure_strategy = "none";
    let exposure_strategy_impact = 0.5;
    let min_unique_players = 1;
    var generated_lineups = [];
    var lineups_summary = [];
    // The entries file (DraftKings Edit Entries / FanDuel CSV Edit) loaded in
    // the Entries File modal, what the server read from it, and the last late swap
    var entries_file = null;
    var entries_info = null;
    var entries_busy = false;
    var swap_result = null;
    let playerSearchString = "";
    let sortableCols = { name: 0, salary: 0, proj: 0, val: 0 };
    export let players;
    export let games;
    export let salary;
    export let slate_id;
    export let upcoming_slates;
    export let roster;
    export let ruleset_name;
    export let league;
    export let position_filters = ["PG", "SG", "SF", "PF", "C"];
    export let premium_membership = true;
    export let placeholder_variances = {};
    // Correlation rules: a defense, pitcher or goalie scores by stopping the
    // players it faces, so it is kept off lineups with its opponents' offense
    const single_game_slate = /Single|Showdown/.test(ruleset_name);
    const ANTI_STACK = {
        nfl: { label: "No defense against my offense", why: "A defense scores when the offense it faces doesn't, so the two cancel out in one lineup.", stoppers: ["DST", "D", "DEF"], stopped: ["QB", "RB", "WR", "TE"] },
        mlb: { label: "No pitcher against my hitters", why: "A pitcher scores by getting the hitters he faces out.", stoppers: ["P"], stopped: ["C", "1B", "2B", "3B", "SS", "OF"] },
        nhl: { label: "No goalie against my skaters", why: "A goalie scores by stopping the skaters he faces.", stoppers: ["G"], stopped: ["C", "W", "D"] },
    };
    let anti_stack = !single_game_slate && league in ANTI_STACK;
    let bring_back = false;
    // a locked defense facing a locked opponent makes the anti-stack impossible
    $: anti_stack_conflict = antiStackConflict(roster);
    function antiStackConflict(current_roster) {
        const rule = ANTI_STACK[league];
        if (!rule) return null;
        const locked = Object.values(current_roster).map((slot) => slot.player).filter(Boolean);
        for (const stopper of locked) {
            if (!stopper.positions.some((p) => rule.stoppers.includes(p))) continue;
            const facing = locked.find((p) => p.team == stopper.opp && p.positions.some((x) => rule.stopped.includes(x)));
            if (facing) return `${stopper.name} is locked against ${facing.name}`;
        }
        return null;
    }
    let num_lineups = "3";
    if (!premium_membership){
        num_lineups = "1";
    }
    let teamStacks = [];
    let gameStacks = [];
    let positionStacks = [];
    let selectedTeam = "all";
    let teams = getTeams(games);
    let mobile = window.innerWidth < 768;
    let startSalary = salary;
    let dragged_player_index = null;
    let players_rostered = 0;
    let roster_size = Object.keys(roster).length;
    let teamGameStrings = gameStrings(games);
    let modal_projection_id;
    let modal_player;
    function showPremiumModal() {
        let myModal = new bootstrap.Modal(
            document.getElementById("premium-modal")
        );
        myModal.show();
    }
    function showPlayerModal(player) {
        modal_projection_id = player.proj_id;
        modal_player = player;
        let myModal = new bootstrap.Modal(
            document.getElementById("player-modal")
        );
        myModal.show();
    }
    onMount(() => {
        sortByX("salary");
    });
    function checkMobile() {
        if (window.innerWidth < 768) {
            mobile = true;
        } else {
            mobile = false;
        }
    }
    window.onresize = checkMobile;
    function sortByX(key, toggle = 1) {
        if (key == "val") {
            sortByValue(toggle);
            return;
        }
        if (sortableCols[key] == 1) {
            sortableCols[key] = -1 * toggle;
        } else {
            sortableCols[key] = 1 * toggle;
        }
        players.sort((a, b) =>
            a[key] > b[key] ? -1 * sortableCols[key] : 1 * sortableCols[key]
        );
        players = players;
        for (var prop in sortableCols) {
            if (prop != key) {
                sortableCols[prop] = 0;
            }
        }
        sortableCols = sortableCols;
    }
    function showSlateSelect() {
        //check if slate_id is in upcoming_slates
        for (let i = 0; i < upcoming_slates.length; i++) {
            if (upcoming_slates[i].slate_id == slate_id) {
                return true;
            }
        }
        return false;
    }
    function sortByValue(toggle = 1) {
        if (sortableCols["val"] == 1) {
            sortableCols["val"] = -1 * toggle;
        } else {
            sortableCols["val"] = 1 * toggle;
        }
        players.sort((a, b) =>
            a.proj / a.salary > b.proj / b.salary
                ? -1 * sortableCols["val"]
                : 1 * sortableCols["val"]
        );
        for (var prop in sortableCols) {
            if (prop != "val") {
                sortableCols[prop] = 0;
            }
        }
        sortableCols = sortableCols;
        players = players;
    }
    async function optimizeLineup() {
        const wanted = parseInt(num_lineups);
        window.showLoadingDiv(`Optimizing Lineups... 0 / ${wanted}`);
        try {
            const response = await fetch(`/optimize/${league}/`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Accept: "application/x-ndjson",
                    "X-CSRFToken": window.getCookie("csrftoken"), //global function
                },
                body: JSON.stringify({
                    ...optimizeSettings(),
                    num_lineups: num_lineups,
                    stream: true,
                }),
            });
            if (!response.ok) {
                throw new Error(`optimize returned ${response.status}`);
            }
            // The server streams one JSON line per lineup, then a summary line,
            // so a big build shows progress instead of a silent spinner
            let lineups = [];
            let summary = [];
            let build_error = null;
            const handleLine = (msg) => {
                if (msg.lineup) {
                    lineups.push(msg);
                    window.showLoadingDiv(`Optimizing Lineups... ${lineups.length} / ${wanted}`);
                } else if (msg.error) {
                    build_error = msg.error;
                } else if (msg.done) {
                    summary = msg.summary;
                }
            };
            await readNdjson(response, handleLine);
            window.hideLoadingDiv();
            if (lineups.length === 0) {
                throw new Error(build_error || "no lineups built");
            }
            lineups.sort((a, b) => b.fantasy_points - a.fantasy_points);
            generated_lineups = lineups;
            lineups_summary = summary;
            swap_result = null;
            if (build_error) {
                alert(`Only ${lineups.length} of ${wanted} lineups fit these settings. Loosen exposure, uniqueness or stacks for more.`);
            }
            let myModal = new bootstrap.Modal(
                document.getElementById("lineup-modal")
            );
            myModal.show();
        } catch (error) {
            window.hideLoadingDiv();
            console.log(error);
            alert("Error Creating Lineups! Please edit your criteria and try again.");
        }
    }
    function optimizeSettings() {
        return {
            roster: roster,
            available_players: players,
            slate_id: slate_id,
            min_unique_players: min_unique_players,
            correlation: { anti_stack: anti_stack && !anti_stack_conflict, bring_back: bring_back },
            ruleset_name: ruleset_name,
            strategy: strategy,
            strategy_impact: strategy_impact,
            exposure_strategy: exposure_strategy,
            exposure_strategy_impact: exposure_strategy_impact,
            gameStacks: gameStacks,
            teamStacks: teamStacks,
            positionStacks: positionStacks,
            games: games,
        };
    }
    // The server streams one JSON object per line; hand each to onMessage as it lands
    async function readNdjson(response, onMessage) {
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";
        const handleLine = (line) => {
            if (line.trim()) onMessage(JSON.parse(line));
        };
        while (true) {
            const { value, done } = await reader.read();
            if (done) break;
            buffer += decoder.decode(value, { stream: true });
            let newline;
            while ((newline = buffer.indexOf("\n")) >= 0) {
                handleLine(buffer.slice(0, newline));
                buffer = buffer.slice(newline + 1);
            }
        }
        handleLine(buffer);
    }
    async function postError(response, fallback) {
        const body = await response.json().catch(() => ({}));
        return new Error(body.error || `${fallback} (${response.status})`);
    }
    // Read an entries file (DraftKings > Edit Entries, FanDuel > CSV Edit) and
    // show what it holds before anything is done with it
    async function loadEntriesFile(file) {
        if (!file) return;
        entries_busy = true;
        try {
            const form = new FormData();
            form.append("entries_file", file);
            form.append("slot_names", JSON.stringify(slotNames()));
            form.append("games", JSON.stringify(games));
            const response = await fetch(`/entries/inspect/${league}/`, {
                method: "POST",
                headers: { "X-CSRFToken": window.getCookie("csrftoken") },
                body: form,
            });
            if (!response.ok) throw await postError(response, "could not read the file");
            entries_info = await response.json();
            entries_file = file;
            swap_result = null;
        } catch (error) {
            console.log(error);
            alert(`Could not read the entries file: ${error.message}`);
        }
        entries_busy = false;
    }
    function onEntriesInput(event) {
        loadEntriesFile(event.target.files[0]);
        event.target.value = "";
    }
    function onEntriesDrop(event) {
        loadEntriesFile(event.dataTransfer.files[0]);
    }
    function clearEntriesFile() {
        entries_file = null;
        entries_info = null;
        swap_result = null;
    }
    function matchupList(games_list) {
        return games_list.map((g) => `${g.away}@${g.home}`).join(", ");
    }
    function slotNames() {
        return Object.values(roster).map((slot) => slot.csv_name);
    }
    function lineupIds(lineup_info) {
        return lineup_info.lineup.map((player) => player.slate_id);
    }
    async function downloadFilledEntries(file, form) {
        form.append("entries_file", file);
        form.append("slot_names", JSON.stringify(slotNames()));
        const response = await fetch(`/entries/fill/${league}/`, {
            method: "POST",
            headers: { "X-CSRFToken": window.getCookie("csrftoken") },
            body: form,
        });
        if (!response.ok) throw await postError(response, "fill failed");
        const blob = await response.blob();
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = file.name || "entries.csv";
        link.click();
        setTimeout(() => URL.revokeObjectURL(link.href), 10000);
        return {
            filled: parseInt(response.headers.get("X-Entries-Filled")),
            total: parseInt(response.headers.get("X-Entries-Total")),
        };
    }
    // Write the built lineups into the loaded entries file, one per entry, in order
    async function fillEntries() {
        try {
            const form = new FormData();
            form.append("lineups", JSON.stringify(generated_lineups.map(lineupIds)));
            const { filled, total } = await downloadFilledEntries(entries_file, form);
            let note = `Filled ${filled} of ${total} entries in ${entries_file.name}. Upload it on ${entries_info.site_name}.`;
            if (total > generated_lineups.length) {
                note += ` The other ${total - filled} entries keep the lineups they had; build ${total} lineups to fill them all.`;
            } else if (generated_lineups.length > total) {
                note += ` Only the top ${total} of your ${generated_lineups.length} lineups were used.`;
            }
            alert(note);
        } catch (error) {
            console.log(error);
            alert(`Could not fill the entries file: ${error.message}`);
        }
    }
    // After a late swap, write each swapped lineup back into the entry it came from
    async function downloadSwappedEntries() {
        try {
            const form = new FormData();
            const by_entry = {};
            for (const lineup_info of generated_lineups) {
                if (lineup_info.entry_index !== undefined) by_entry[lineup_info.entry_index] = lineupIds(lineup_info);
            }
            form.append("lineups_by_entry", JSON.stringify(by_entry));
            const { filled } = await downloadFilledEntries(entries_file, form);
            alert(`${filled} entries written to ${entries_file.name}. Upload it on ${entries_info.site_name}.`);
        } catch (error) {
            console.log(error);
            alert(`Could not write the entries file: ${error.message}`);
        }
    }
    // Rebuild every entry in the loaded entries file around the players whose games have started
    async function lateSwap() {
        window.showLoadingDiv("Late swapping entries... 0");
        try {
            const form = new FormData();
            form.append("entries_file", entries_file);
            form.append("settings", JSON.stringify(optimizeSettings()));
            const response = await fetch(`/entries/swap/${league}/`, {
                method: "POST",
                headers: {
                    Accept: "application/x-ndjson",
                    "X-CSRFToken": window.getCookie("csrftoken"),
                },
                body: form,
            });
            if (!response.ok) throw await postError(response, "late swap failed");
            let lineups = [];
            let result = null;
            let build_error = null;
            await readNdjson(response, (msg) => {
                if (msg.lineup) {
                    lineups.push(msg);
                    window.showLoadingDiv(`Late swapping entries... ${lineups.length}`);
                } else if (msg.error) {
                    build_error = msg.error;
                } else if (msg.done) {
                    result = msg;
                }
            });
            window.hideLoadingDiv();
            if (lineups.length === 0) {
                throw new Error(build_error || (result && result.skipped.length ? result.skipped[0].reason : "no entries could be rebuilt"));
            }
            result.build_error = build_error;
            swap_result = result;
            generated_lineups = lineups;
            lineups_summary = result.summary;
        } catch (error) {
            window.hideLoadingDiv();
            console.log(error);
            alert(`Late swap failed: ${error.message}`);
        }
    }
    function positionsValid(player) {
        for (var pos of player.positions) {
            for (var key in roster) {
                if (
                    roster[key].fillable_by.includes(pos) &&
                    roster[key].player == null
                ) {
                    return key;
                }
            }
        }
        return false;
    }
    function salaryValid(player) {
        return canRosterAnywhere(player);
    }
    // Helpers for slot-based salary multipliers (fallback to points multiplier if salary_multiplier is missing)
    function getSlotSalaryMultiplier(pos) {
        const slot = roster[pos] || {};
        if (slot.salary_multiplier != null) return slot.salary_multiplier;
        if (slot.multiplier != null) return slot.multiplier;
        return 1;
    }
    function calcSlotCost(player, pos) {
        return Math.round(player.salary * getSlotSalaryMultiplier(pos));
    }
    function displayedSlotSalary(pos) {
        const slot = roster[pos];
        if (!slot || !slot.player) return 0;
        return calcSlotCost(slot.player, pos);
    }

    // Find all open, fillable, and affordable slots for a player
    function affordableSlotsFor(player) {
        const options = [];
        for (const key in roster) {
            const slot = roster[key];
            if (!slot || slot.player != null) continue;
            for (const dp_pos of player.positions) {
                if (slot.fillable_by.includes(dp_pos)) {
                    const cost = calcSlotCost(player, key);
                    if (cost <= salary) {
                        options.push({ key, cost });
                        break;
                    }
                }
            }
        }
        return options;
    }
    // Choose the cheapest affordable slot for a player
    function findAffordableSlot(player) {
        const options = affordableSlotsFor(player);
        if (options.length === 0) return false;
        options.sort((a, b) => {
            const am = (roster[a.key] && roster[a.key].multiplier != null) ? roster[a.key].multiplier : 1;
            const bm = (roster[b.key] && roster[b.key].multiplier != null) ? roster[b.key].multiplier : 1;
            if (players_rostered === 0 && am !== bm) return bm - am; // favor highest multiplier (e.g., MVP) when nothing is locked
            return a.cost - b.cost; // otherwise cheapest slot
        });
        return options[0].key;
    }
    // Can the player be rostered in any valid slot given current salary?
    function canRosterAnywhere(player) {
        return affordableSlotsFor(player).length > 0;
    }
    function includePlayer(index) {
        players[index].considered = true;
    }
    function excludePlayer(index) {
        players[index].considered = false;
    }
    function rosterPlayer(index) {
        let player = players[index];
        let pos = findAffordableSlot(player);
        if (!pos) {
            alert("You have no open roster slots for this player!");
            return;
        }
        const cost = calcSlotCost(player, pos);
        players.splice(index, 1);
        players = players;
        roster[pos].player = player;
        salary -= cost;
        projFP += player.proj * roster[pos].multiplier;
        projFP = Math.round(projFP * 10) / 10;
        players_rostered += 1;
    }
    function clearRoster() {
        for (var pos in roster) {
            if (roster[pos].player === null) {
                continue;
            } else {
                players.push(roster[pos].player);
                roster[pos].player = null;
            }
        }
        roster = roster;
        players = players;
        salary = startSalary;
        projFP = 0;
        players_rostered = 0;
        for (var prop in sortableCols) {
            if (sortableCols[prop] != 0) {
                sortByX(prop, -1);
            }
        }
    }
    function unrosterPlayer(pos) {
        if (roster[pos].player === null) {
            return;
        }
        let player = roster[pos].player;
        players.push(player);
        players = players;
        salary += calcSlotCost(player, pos);
        projFP -= player.proj * roster[pos].multiplier;
        projFP = Math.round(projFP * 10) / 10;
        roster[pos].player = null;
        for (var prop in sortableCols) {
            if (sortableCols[prop] != 0) {
                sortByX(prop, -1);
            }
        }
        players_rostered -= 1;
    }
    function drag(event, index) {
        dragged_player_index = index;
        let dragged_player = players[index];
        setTimeout(function () {
            event.target.classList.add("hide");
        });
        for (var pos of dragged_player.positions) {
            // check if pos starts with a number
            if (pos == "1B") {
                pos = "\\31 B";
            }
            else if(pos == "C/1B"){
                pos = "C\\/1B";
            }
            else if(pos == "2B"){
                pos = "\\32 B";
            }
            else if(pos == "3B"){
                pos = "\\33 B";
            }
            document.querySelectorAll(`[class*="${pos}"]`).forEach((ref) => {
                ref.classList.add("highlight");
            });
        }
        const newDiv = document.createElement("div");
        newDiv.classList.add("drag-ele");
        newDiv.classList.add("card");
        newDiv.classList.add("px-2");
        newDiv.classList.add("d-flex");
        newDiv.classList.add("flex-row");
        newDiv.style.borderRadius = "10px";
        const dragImg = document.createElement("img");
        dragImg.setAttribute("src", dragged_player.img_thumb);
        dragImg.classList.add("img-fluid");
        dragImg.style.height = "100px";
        const playerDiv = document.createElement("div");
        playerDiv.classList.add("d-flex");
        playerDiv.classList.add("flex-column");
        playerDiv.classList.add("align-items-center");
        playerDiv.classList.add("justify-content-center");
        playerDiv.classList.add("text-center");
        const textDiv = document.createElement("div");
        textDiv.classList.add("d-flex");
        textDiv.classList.add("flex-column");
        textDiv.classList.add("align-items-center");
        textDiv.classList.add("justify-content-center");
        textDiv.classList.add("text-center");
        const imgDiv = document.createElement("div");
        const nameDiv = document.createElement("div");
        const salaryDiv = document.createElement("div");
        salaryDiv.classList.add("text-success");
        const projDiv = document.createElement("div");
        salaryDiv.style.whiteSpace = "nowrap";
        projDiv.style.whiteSpace = "nowrap";
        projDiv.classList.add("text-primary");
        const nameText = document.createTextNode(`${dragged_player.name}`);
        const salaryText = document.createTextNode(
            `💸${dragged_player.salary}`
        );
        const projText = document.createTextNode(`📊${dragged_player.proj}`);

        imgDiv.appendChild(dragImg);
        nameDiv.appendChild(nameText);
        salaryDiv.appendChild(salaryText);
        projDiv.appendChild(projText);
        playerDiv.appendChild(imgDiv);
        textDiv.appendChild(nameDiv);
        textDiv.appendChild(salaryDiv);
        textDiv.appendChild(projDiv);
        newDiv.appendChild(playerDiv);
        newDiv.appendChild(textDiv);
        newDiv.style.position = "absolute";
        newDiv.style.top = "-1000px";
        document.body.appendChild(newDiv);
        event.dataTransfer.setDragImage(newDiv, 230, 10);
    }
    function dragend(event) {
        setTimeout(function () {
            event.target.classList.remove("hide");
        });
        document.querySelectorAll(".highlight").forEach((ele) => {
            ele.classList.remove("highlight");
        });
    }
    function drop(pos) {
        document.querySelectorAll(".highlight").forEach((ele) => {
            ele.classList.remove("highlight");
        });
        let dragged_player = players[dragged_player_index];
        const cost = calcSlotCost(dragged_player, pos);
        if (cost > salary) {
            alert("Not enough salary to roster this player!");
            return;
        }
        players.splice(dragged_player_index, 1);
        players = players;
        roster[pos].player = dragged_player;
        salary -= cost;
        projFP += dragged_player.proj * roster[pos].multiplier;
        projFP = Math.round(projFP * 10) / 10;
        players_rostered += 1;
    }
    function allowDrop(event, pos) {
        let dragged_player = players[dragged_player_index];
        let allowable = false;

        for (var dp_pos of dragged_player.positions) {
            if (roster[pos].fillable_by.includes(dp_pos)) {
                allowable = true;
            }
        }
        if (!allowable) {
            return;
        }
        // Ensure sufficient salary for this slot considering multiplier
        if (calcSlotCost(dragged_player, pos) > salary) {
            return;
        }
        if (pos == "1B") {
            pos = "\\31 B";
        }
        else if(pos == "C/1B"){
            pos = "C\\/1B";
        }
        else if(pos == "2B"){
            pos = "\\32 B";
        }
        else if(pos == "3B"){
            pos = "\\33 B";
        }
        event.target.classList.add("over");
        event.preventDefault();
    }
    function handleLeave(event) {
        event.target.classList.remove("over");
    }
    function createCSVContent(include_header = true, delimiter = ",") {
        let csv = "";
        //add the position names to top row of the csv
        if (include_header) {
            for (var pos in roster) {
                csv += `${roster[pos].csv_name}${delimiter}`;
            }
            csv += "\n";
        }
        //add player ids from generated_lineups to csv
        for (var lineup_info of generated_lineups) {
            for (var player of lineup_info.lineup) {
                csv += `${player.slate_id}${delimiter}`;
            }
            csv += "\n";
        }
        return csv;
    }
    function createCSV() {
        let csv = createCSVContent();
        //download csv
        var hiddenElement = document.createElement("a");
        hiddenElement.href = "data:text/csv;charset=utf-8," + encodeURI(csv);
        hiddenElement.target = "_blank";
        //current timestamp
        let timestamp = new Date().getTime();
        hiddenElement.download = `generated_linups-${timestamp}.csv`;
        hiddenElement.click();
    }
    function copyToClipboard() {
        let csv = createCSVContent(false, "\t");
        navigator.clipboard.writeText(csv);
    }
</script>

<div
    class="modal fade"
    id="premium-modal"
    data-bs-keyboard="false"
    tabindex="-1"
    aria-labelledby="staticBackdropLabel"
    aria-hidden="true"
>
    <div class="modal-dialog modal-dialog-centered modal-xl">
        <div class="modal-content">
            <div class="modal-body p-0">
                <PremiumOnly
                    title_text={"GigaStuds Only!"}
                    text={"The ability to generate additional lineups is only available to GigaStuds. <a class='text-decoration-none' href='/purchase-premium'>Become a GigaStud</a> to gain access to all DFStud features!!"}
                    logo_url={window.logo_url}
                />
            </div>
            <div class="modal-footer">
                <div class="btn-group" role="group">
                    <button
                        type="button"
                        class="btn btn-secondary"
                        data-bs-dismiss="modal">Close</button
                    >
                </div>
            </div>
        </div>
    </div>
</div>

<PlayerModal
    placeholder_variances={placeholder_variances}
    player={modal_player}
    proj_id={modal_projection_id}
    premium_membership={premium_membership}
    league={league}
/>
{#if generated_lineups}
    <div
        class="modal fade"
        id="lineup-modal"
        data-bs-backdrop="static"
        data-bs-keyboard="false"
        tabindex="-1"
        aria-labelledby="staticBackdropLabel"
        aria-hidden="true"
    >
        <div class="modal-dialog modal-dialog-centered modal-xl">
            <div class="modal-content" style="background: rgb(233, 236, 239);">
                <div class="modal-header">
                    <h5 class="modal-title" id="staticBackdropLabel">
                        {#if swap_result}
                            Late Swap Results
                        {:else}
                            Generated Lineups
                        {/if}
                    </h5>
                    <button
                        type="button"
                        class="btn-close"
                        data-bs-dismiss="modal"
                        aria-label="Close"
                    />
                </div>
                <div class="modal-body">
                    <Swiper
                        modules={[Navigation, Pagination, Scrollbar, A11y]}
                        spaceBetween={10}
                        navigation={!mobile}
                        pagination={{ type: "progressbar", clickable: true }}
                        breakpoints={{
                            320: {
                                slidesPerView: 1,
                                spaceBetween: 10,
                            },
                            768: {
                                slidesPerView: 1,
                                spaceBetween: 10,
                            },
                            1024: {
                                slidesPerView: 2,
                                spaceBetween: 10,
                            },
                            1280: {
                                slidesPerView: 2,
                                spaceBetween: 10,
                            },
                            1920: {
                                slidesPerView: 3,
                                spaceBetween: 10,
                            },
                        }}
                    >
                        {#each generated_lineups as lineup}
                            <SwiperSlide
                                ><Lineup lineup_details={lineup} /></SwiperSlide
                            >
                        {/each}
                    </Swiper>
                </div>
                <div class="modal-footer">
                    <div class="btn-group flex-wrap" role="group">
                        <button
                            type="button"
                            class="btn btn-outline-secondary"
                            data-bs-dismiss="modal"
                            >Generate More Lineups</button
                        >
                        <button
                            class="btn btn-secondary"
                            data-bs-target="#summary-modal"
                            data-bs-toggle="modal"
                            data-bs-dismiss="modal">Lineups Summary</button
                        >
                        <button
                            type="button"
                            class="btn btn-outline-primary"
                            on:click={() => copyToClipboard()}>📋Copy to Clipboard</button
                        >
                        <button
                            type="button"
                            class="btn btn-primary"
                            on:click={() => createCSV()}>💾Export to CSV</button
                        >
                        {#if premium_membership}
                            <button
                                type="button"
                                class="btn btn-success"
                                title="Write these lineups into your DraftKings or FanDuel entries file"
                                data-bs-target="#entries-modal"
                                data-bs-toggle="modal"
                                data-bs-dismiss="modal">📂Entries File</button
                            >
                        {/if}
                    </div>
                </div>
            </div>
        </div>
    </div>
    <div
        class="modal fade"
        id="summary-modal"
        aria-hidden="true"
        aria-labelledby="exampleModalToggleLabel2"
        tabindex="-1"
    >
        <div
            class="modal-dialog modal-dialog-centered modal-dialog-scrollable modal-xl"
        >
            <div class="modal-content">
                <div class="modal-header">
                    <h5 class="modal-title" id="exampleModalToggleLabel2">
                        Summary
                    </h5>
                    <button
                        type="button"
                        class="btn-close"
                        data-bs-dismiss="modal"
                        aria-label="Close"
                    />
                </div>
                <div class="modal-body">
                    <h5>Lineups Generated: {generated_lineups.length}</h5>
                    <h5>Distinct Players Played: {lineups_summary.length}</h5>
                    {#if lineups_summary.length > 0}
                        <h5>
                            Most Played: {lineups_summary[0][1][0].name} | {lineups_summary[1][1][0]
                                .name} | {lineups_summary[2][1][0].name}
                        </h5>
                        <h5>
                            Least Played: {lineups_summary[
                                lineups_summary.length - 1
                            ][1][0].name} | {lineups_summary[
                                lineups_summary.length - 2
                            ][1][0].name} | {lineups_summary[
                                lineups_summary.length - 3
                            ][1][0].name}
                        </h5>
                    {/if}
                    <!--table of the summary-->
                    <table class="table table-hover">
                        <thead
                            ><tr>
                                <th>Player</th><th>Exposure</th><th
                                    >Times Rostered</th
                                >
                            </tr></thead
                        >
                        <tbody>
                            {#each lineups_summary as player}
                                <tr
                                    ><td class="pb-0"
                                        ><img
                                            alt="{player[1][0].name}"
                                            loading="lazy"
                                            height="50"
                                            src={player[1][0].img_thumb}
                                            onerror="this.src='https://dfstud.nyc3.digitaloceanspaces.com/dfstud/static/fantasy/img/brand/horse_default_profile2.png';"
                                        />{player[1][0].name}</td
                                    ><td
                                        >{(
                                            player[1][1] /
                                            generated_lineups.length
                                        ).toFixed(2)}</td
                                    ><td>{player[1][1]}</td></tr
                                >
                            {/each}
                        </tbody>
                    </table>
                </div>
                <div class="modal-footer">
                    <button
                        class="btn btn-primary"
                        data-bs-target="#lineup-modal"
                        data-bs-toggle="modal"
                        data-bs-dismiss="modal">Back to Lineups</button
                    >
                </div>
            </div>
        </div>
    </div>
{/if}
<div
    class="modal fade"
    id="entries-modal"
    tabindex="-1"
    aria-labelledby="entries-modal-label"
    aria-hidden="true"
>
    <div class="modal-dialog modal-dialog-centered modal-lg">
        <div class="modal-content">
            <div class="modal-header">
                <h5 class="modal-title" id="entries-modal-label">📂Entries File</h5>
                <button
                    type="button"
                    class="btn-close"
                    data-bs-dismiss="modal"
                    aria-label="Close"
                />
            </div>
            <div class="modal-body">
                {#if !entries_info}
                    <p>
                        Register your entries on the site first, then download the entries file:
                        <b>DraftKings</b> Lineups → Edit Entries → Download (DKEntries.csv), or
                        <b>FanDuel</b> Upcoming → CSV Edit → Download. Upload it here to fill it with
                        built lineups, or to late swap it once games have started.
                    </p>
                    <div
                        class="border rounded p-4 text-center bg-light"
                        on:dragover|preventDefault
                        on:drop|preventDefault={onEntriesDrop}
                    >
                        <div class="mb-2">Drop the file here, or</div>
                        <label class="btn btn-primary" for="entries-file-input">{entries_busy ? "Reading..." : "Choose File"}</label>
                        <input
                            id="entries-file-input"
                            type="file"
                            accept=".csv,text/csv"
                            class="d-none"
                            on:change={onEntriesInput}
                        />
                        <div class="form-text mt-2">Don't open the file in Excel first; it mangles the player ids.</div>
                    </div>
                {:else}
                    <div class="d-flex justify-content-between align-items-start mb-3">
                        <div>
                            <div class="h5 mb-1">{entries_info.site_name} · {entries_info.file_name}</div>
                            <div>
                                {entries_info.entries} {entries_info.entries == 1 ? "entry" : "entries"} in
                                {entries_info.contests.map((c) => `${c.name} (${c.entries})`).join(", ")}
                            </div>
                            {#if entries_info.roster_error}
                                <div class="text-danger">⚠️ {entries_info.roster_error}</div>
                            {:else}
                                <div class="text-success">✅ Roster matches this slate: {entries_info.slot_names.join(", ")}</div>
                            {/if}
                            <div>
                                {entries_info.started_games} of {entries_info.games.length} games started{entries_info.started_games > 0 ? `: ${matchupList(entries_info.games.filter((g) => g.started))}` : ""}
                            </div>
                        </div>
                        <button type="button" class="btn btn-sm btn-outline-secondary text-nowrap ms-2" on:click={() => clearEntriesFile()}>Change file</button>
                    </div>
                    <div class="row g-3">
                        <div class="col-md-6">
                            <div class="card h-100">
                                <div class="card-body d-flex flex-column">
                                    <h5 class="card-title">📤 Fill with built lineups</h5>
                                    <p class="card-text">
                                        Writes your built lineups into the entries, one per entry in order, and
                                        downloads the file ready to upload back to {entries_info.site_name}.
                                    </p>
                                    <p class="card-text small text-muted">
                                        {#if generated_lineups.length == 0}
                                            Build lineups first with 📈Optimize.
                                        {:else}
                                            {generated_lineups.length} lineups ready for {entries_info.entries} entries.
                                            {#if generated_lineups.length < entries_info.entries}
                                                The other {entries_info.entries - generated_lineups.length} keep their current lineups.
                                            {:else if generated_lineups.length > entries_info.entries}
                                                The top {entries_info.entries} are used.
                                            {/if}
                                        {/if}
                                    </p>
                                    <button
                                        type="button"
                                        class="btn btn-primary mt-auto"
                                        disabled={generated_lineups.length == 0 || entries_info.roster_error}
                                        on:click={() => fillEntries()}>Fill &amp; Download</button
                                    >
                                </div>
                            </div>
                        </div>
                        <div class="col-md-6">
                            <div class="card h-100">
                                <div class="card-body d-flex flex-column">
                                    <h5 class="card-title">🔄 Late swap</h5>
                                    <p class="card-text">
                                        Keeps every player whose game has started where he is and rebuilds the open
                                        slots of each entry with the current projections and your Advanced Settings.
                                    </p>
                                    {#if swap_result}
                                        <p class="card-text small text-muted">
                                            {swap_result.changed} of {swap_result.built} entries changed.
                                            {#if swap_result.skipped.length}
                                                {swap_result.skipped.length} skipped: {swap_result.skipped.slice(0, 3).map((s) => `#${s.entry_id} (${s.reason})`).join("; ")}
                                            {/if}
                                            {#if swap_result.build_error}
                                                Stopped early: {swap_result.build_error}
                                            {/if}
                                        </p>
                                        <div class="btn-group mt-auto">
                                            <button
                                                type="button"
                                                class="btn btn-outline-primary"
                                                data-bs-target="#lineup-modal"
                                                data-bs-toggle="modal"
                                                data-bs-dismiss="modal">Review Lineups</button
                                            >
                                            <button type="button" class="btn btn-success" on:click={() => downloadSwappedEntries()}>Download Entries File</button>
                                        </div>
                                    {:else}
                                        <p class="card-text small text-muted">
                                            {#if entries_info.started_games == 0}
                                                Available once a game on this slate has started.
                                            {:else}
                                                Players in {matchupList(entries_info.games.filter((g) => g.started))} stay locked; everyone else is up for review.
                                            {/if}
                                        </p>
                                        <button
                                            type="button"
                                            class="btn btn-primary mt-auto"
                                            disabled={entries_info.started_games == 0 || entries_info.roster_error}
                                            on:click={() => lateSwap()}>Late Swap</button
                                        >
                                    {/if}
                                </div>
                            </div>
                        </div>
                    </div>
                {/if}
            </div>
        </div>
    </div>
</div>
<div
    class="modal fade"
    id="settings-modal"
    tabindex="-1"
    aria-labelledby="settings-modal-label"
    aria-hidden="true"
>
    <div class="modal-dialog modal-xl">
        <div class="modal-content">
            <div class="modal-header">
                <h5 class="modal-title" id="settings-modal-label">
                    ⚙️Advanced Settings
                </h5>
                <button
                    type="button"
                    class="btn-close"
                    data-bs-dismiss="modal"
                    aria-label="Close"
                />
            </div>
            <div class="modal-body">
                {#if premium_membership}
                    <h4>Fantasy Points Strategy</h4>
                    <div class="form-check">
                        <input
                            class="form-check-input"
                            type="radio"
                            name="fp_strategy"
                            bind:group={strategy}
                            id="inlineRadio1"
                            value={"projection"}
                        />
                        <label class="form-check-label" for="inlineRadio1"
                            >Projection only</label
                        >
                    </div>
                    <div class="form-check">
                        <input
                            class="form-check-input"
                            type="radio"
                            name="fp_strategy"
                            bind:group={strategy}
                            id="inlineRadio2"
                            value={"random"}
                        />
                        <label class="form-check-label" for="inlineRadio2"
                            >Randomization</label
                        >
                        <button
                            type="button"
                            class="rounded"
                            data-bs-toggle="tooltip"
                            data-bs-placement="top"
                            title="Randomization adds some deviation for players projection for creating less optimized but more randomized lineups. Low sets the max deviation to 12%, Mid to 18%, and High to 24%"
                            >❔</button
                        >
                    </div>
                    <div class="form-check">
                        <input
                            class="form-check-input"
                            type="radio"
                            name="fp_strategy"
                            bind:group={strategy}
                            id="inlineRadio3"
                            value={"progressive"}
                        />
                        <label class="form-check-label" for="inlineRadio3"
                            >Progressive</label
                        >
                        <button
                            type="button"
                            class="rounded"
                            data-bs-toggle="tooltip"
                            data-bs-placement="top"
                            title="Progressive increases fantasy points for each player that wasn’t used in the previous lineup by some specified percent (Low 1%, Mid 2%, High 3%) of original fantasy points. It works cumulatively so fantasy points will be greater if the player wasn’t used in the lineup multiple times. After the player will be selected to lineup his points will be reset to the original value. This method will automatically exclude players with non-viable value projections."
                            >❔</button
                        >
                    </div>
                    {#if strategy != "projection"}
                        <!--slider to select low medium high for strategy impact-->
                        <div class="form-group">
                            <label for="customRange2" class="form-label h6"
                                >Strategy Impact -
                                {#if strategy_impact == 0}
                                    Low
                                {:else if strategy_impact == 1}
                                    Medium
                                {:else if strategy_impact == 2}
                                    High
                                {/if}
                            </label>
                            <input
                                type="range"
                                bind:value={strategy_impact}
                                class="form-range"
                                min="0"
                                max="2"
                                id="customRange2"
                            />
                        </div>
                    {/if}
                    <hr />
                    <h4>Exposure Strategy</h4>
                    <div class="form-check">
                        <input
                            class="form-check-input"
                            type="radio"
                            name="exposure_strategy"
                            bind:group={exposure_strategy}
                            id="exposeRadio1"
                            value={"none"}
                        />
                        <label class="form-check-label" for="exposeRadio1"
                            >None</label
                        >
                    </div>
                    <div class="form-check">
                        <input
                            class="form-check-input"
                            type="radio"
                            name="exposure_strategy"
                            bind:group={exposure_strategy}
                            id="exposeRadio2"
                            value={"exposure"}
                        />
                        <label class="form-check-label" for="exposeRadio2"
                            >Limit Exposure</label
                        >
                        <button
                            type="button"
                            class="rounded"
                            data-bs-toggle="tooltip"
                            data-bs-placement="top"
                            title="Limiting Exposure limits the maximum percentage of lineups that a player can appear in. Note: Locking a player ignores maximum exposure and they will still appear in every lineup."
                            >❔</button
                        >
                    </div>
                    {#if exposure_strategy != "none"}
                        <!--slider to select low medium high for strategy impact-->
                        <div class="form-group">
                            <label for="customRange3" class="form-label h6"
                                >Max Exposure - {exposure_strategy_impact}</label
                            >
                            <input
                                type="range"
                                bind:value={exposure_strategy_impact}
                                class="form-range"
                                min="0"
                                step=".01"
                                max="1"
                                id="customRange3"
                            />
                        </div>
                    {/if}
                    <h4>Lineup Diversity</h4>
                    <div class="form-group">
                        <label for="min-unique-range" class="form-label h6"
                            >Min Unique Players Between Lineups - {min_unique_players}</label
                        >
                        <input
                            type="range"
                            bind:value={min_unique_players}
                            class="form-range"
                            min="1"
                            step="1"
                            max="4"
                            id="min-unique-range"
                        />
                        <p class="text-muted small mb-0">Every lineup must differ from every other lineup by at least this many players. Most tournament builders run 2 or 3.</p>
                    </div>
                    {#if league in ANTI_STACK}
                        <hr />
                        <h4>Correlation</h4>
                        <div class="form-check">
                            <input
                                class="form-check-input"
                                type="checkbox"
                                id="anti-stack"
                                bind:checked={anti_stack}
                                disabled={anti_stack_conflict != null}
                            />
                            <label class="form-check-label" for="anti-stack">{ANTI_STACK[league].label}</label>
                        </div>
                        <p class="text-muted small mb-0">
                            {#if anti_stack_conflict}
                                Off for this build: {anti_stack_conflict}.
                            {:else if single_game_slate}
                                Every player here is in the one game, so this keeps a {league == "nfl" ? "defense" : league == "mlb" ? "pitcher" : "goalie"} off any lineup with the other side's players. Off by default for single game slates.
                            {:else}
                                {ANTI_STACK[league].why}
                            {/if}
                        </p>
                        {#if league == "nfl" && !single_game_slate}
                            <div class="form-check mt-2">
                                <input class="form-check-input" type="checkbox" id="bring-back" bind:checked={bring_back} />
                                <label class="form-check-label" for="bring-back">Bring back a WR or TE from the other side with my QB</label>
                            </div>
                            <p class="text-muted small mb-0">A shootout lifts both passing games; the bring-back keeps a piece of the other one.</p>
                        {/if}
                    {/if}
                        <hr />
                        <h4 class="text-center">Stacking</h4>
                        {#if games.length > 1}
                        <div>
                            <h5>Game Stack</h5>
                            <p>Specify how many 
                            {#if league == "mlb"}
                                batters
                            {:else}
                                players
                            {/if} you want from the same game</p>
                            {#each gameStacks as gs}
                                <form class="row row-cols-lg-auto g-3 align-items-center mb-1 justify-content-center">
                                    <div class="col-12">At Least</div>
                                    <div class="col-12">
                                        <input min="2" max="8" class="form-control" bind:value={gs.min} type="number">
                                    </div>
                                    <div class="col-12">
                                    {#if league == "mlb"}
                                        batter(s)
                                    {:else}
                                        player(s)
                                    {/if} from the same game with at least</div>
                                    <div class="col-12">
                                        <input min="1" max="4" class="form-control" bind:value={gs.min_each_team} type="number">
                                    </div>
                                    <div class="col-12">
                                    {#if league == "mlb"}
                                        batter(s)
                                    {:else}
                                        player(s)
                                    {/if} from each team</div>
                                    
                                    <div style="cursor:pointer;" on:click={() => {gameStacks = gameStacks.filter(function(item){return item !== gs})}} class="col-12">❌</div>
                                </form>
                            {/each}
                            <div class="d-flex">
                                {#if gameStacks.length == 0}
                                <button
                                type="button"
                                class="btn btn-primary mx-auto"
                                on:click={() => {gameStacks=[];gameStacks.push({min: 2, game: games[0].game_id, min_each_team: 1}); gameStacks=gameStacks;}}
                                >Add a Game Stack</button>
                                {/if}
                            </div>
                        </div>
                        <hr>
                        {/if}
                        <div>
                            <h5>Team Stacks</h5>
                            <p>Specify how many 
                            {#if league == "mlb"}
                                batters
                            {:else}
                                players
                            {/if} you want from certain teams</p>
                            {#each teamStacks as ts}
                                <form class="row row-cols-lg-auto g-3 align-items-center mb-1 justify-content-center">
                                    <div class="col-12">At Least</div>
                                    <div class="col-12">
                                        <input min="1" max="4" class="form-control" bind:value={ts.min} type="number">
                                    </div>
                                    <div class="col-12">
                                        {#if league == "mlb"}
                                            batter(s)
                                        {:else}
                                            player(s)
                                        {/if}
                                     from the</div>
                                    <div class="col-12">
                                    <select class="form-select" bind:value={ts.team}>
                                        {#each teams as team}
                                            <option value={team.short_name}
                                                >{team.city} {team.mascot}</option
                                            >
                                        {/each}
                                        </select>
                                    </div>
                                    <div style="cursor:pointer;" on:click={() => {teamStacks = teamStacks.filter(function(item){return item !== ts})}} class="col-12">❌</div>
                                </form>
                            {/each}
                            <div class="d-flex">
                                <button
                                type="button"
                                class="btn btn-primary mx-auto"
                                on:click={() => {teamStacks.push({min: 2, team: teams[0].short_name}); teamStacks=teamStacks;}}
                                >Add a Team Stack</button>
                            </div>
                        </div>
                        <hr>

                        <div>
                            <h5>Position Stacks</h5>
                            <p>The Optimizer will create lineups with 
                                {#if league == "mlb"}
                                    batters
                                {:else}
                                    players
                                {/if}
                            on the same team that play the specified positions. You can select more than one position per dropdown.</p>
                            {#each positionStacks as ps}
                                <form class="row row-cols-lg-auto g-3 align-items-center mb-1 justify-content-center">
                                    {#each ps.positions as pos}
                                        <div class="col-12">
                                            
                                            <select class="form-select" bind:value={pos} multiple>
                                                {#if league == "nfl"}
                                                <option value="QB">QB</option>
                                                <option value="RB">RB</option>
                                                <option value="WR">WR</option>
                                                <option value="TE">TE</option>
                                                <option value="DST">DST</option>
                                                {/if}
                                                {#if league == "nba"}
                                                <option value="PG">PG</option>
                                                <option value="SG">SG</option>
                                                <option value="SF">SF</option>
                                                <option value="PF">PF</option>
                                                <option value="C">C</option>
                                                {/if}
                                                {#if league == "mlb"}
                                                <option value="P">P</option>
                                                <option value="C">C</option>
                                                <option value="\1B">1B</option>
                                                <option value="\2B">2B</option>
                                                <option value="\3B">3B</option>
                                                <option value="SS">SS</option>
                                                <option value="OF">OF</option>
                                                {/if}
                                                {#if league == "nhl"}
                                                <option value="C">C</option>
                                                <option value="W">W</option>
                                                <option value="D">D</option>
                                                <option value="G">G</option>
                                                {/if}
                                            </select>
                                        </div>
                                    {/each}
                                    <div class="col-12">
                                        <button
                                        type="button"
                                        class="btn btn-primary mx-auto"
                                        on:click={() => {
                                            if (league == "nfl"){
                                                ps.positions.push("QB"); ps.positions=ps.positions;
                                            }
                                            if (league == "nba"){
                                                ps.positions.push("PG"); ps.positions=ps.positions;
                                            }
                                            if (league == "mlb"){
                                                ps.positions.push("C"); ps.positions=ps.positions;
                                            }
                                            if (league == "nhl"){
                                                ps.positions.push("C"); ps.positions=ps.positions;
                                            }
                                            }}
                                        >Add a Position Group</button>
                                    </div>
                                    <div style="cursor:pointer;" on:click={() => {positionStacks = positionStacks.filter(function(item){return item !== ps})}} class="col-12">❌</div>
                                </form>
                            {/each}
                            <div class="d-flex">
                                <button
                                type="button"
                                class="btn btn-primary mx-auto"
                                on:click={() => {
                                    let my_stack = {positions: []};
                                    if (league == "nfl"){
                                        my_stack.positions.push("QB");
                                        my_stack.positions.push("WR");
                                    }
                                    if (league == "nba"){
                                        my_stack.positions.push("PG");
                                        my_stack.positions.push("PF");
                                    }
                                    if (league == "mlb"){
                                        my_stack.positions.push("C");
                                        my_stack.positions.push("OF");
                                    }
                                    if (league == "nhl"){
                                        my_stack.positions.push("C");
                                        my_stack.positions.push("W");
                                    }
                                    positionStacks.push(my_stack);
                                    positionStacks=positionStacks;
                                }}
                                >Add a Position Stack</button>
                            </div>
                        </div>
                {:else}
                    <PremiumOnly
                        title_text={"GigaStuds Only!"}
                        text={"Advanced Lineup Settings are only available to GigaStuds. <a class='text-decoration-none' href='/purchase-premium'>Become a GigaStud</a> to gain access to all DFStud features!!"}
                        logo_url={window.logo_url}
                    />
                {/if}
            </div>

            <div class="modal-footer">
                <button
                    type="button"
                    class="btn btn-secondary"
                    data-bs-dismiss="modal">Close</button
                >
            </div>
        </div>
    </div>
</div>
<div
    style="background-color:#e9ecef;"
    class="bg-gradient shadow border-top border-bottom py-2 mb-4"
>
    <h1 class="ps-2 display-6 lh-1">Games</h1>
    <Swiper
        modules={[Navigation, Pagination, Scrollbar, A11y]}
        spaceBetween={10}
        navigation={!mobile}
        pagination={{ clickable: true }}
        breakpoints={{
            320: {
                slidesPerView: 1,
                spaceBetween: 10,
            },
            768: {
                slidesPerView: 2,
                spaceBetween: 10,
            },
            1024: {
                slidesPerView: 3,
                spaceBetween: 10,
            },
            1280: {
                slidesPerView: 4,
                spaceBetween: 10,
            },
            1920: {
                slidesPerView: 5,
                spaceBetween: 10,
            },
        }}
    >
        {#each games as game}
            <SwiperSlide
                ><GameCard
                    home_team={game.home_team}
                    away_team={game.away_team}
                    game_time={game.game_time}
                    lines={game.lines}
                /></SwiperSlide
            >
        {/each}
    </Swiper>
</div>
<div class="container-fluid">
    <div class="row">
        <div class="col-lg-8">
            <div class="row">
                {#if showSlateSelect()}
                    <div class="col-lg-5 mb-1">
                        <label for="slate-select">Choose a Slate</label>
                        <select
                            :value={slate_id}
                            on:change={() => {
                                window.location =
                                    document.getElementById(
                                        "slate-select"
                                    ).value;
                            }}
                            class="form-select form-select-lg"
                            id="slate-select"
                        >
                            {#each upcoming_slates as slate}
                                {#if slate.slate_id == slate_id}
                                    <option value={slate.slate_id} selected
                                        >{slate.slate_name}</option
                                    >
                                {:else}
                                    <option value={slate.slate_id}
                                        >{slate.slate_name}</option
                                    >
                                {/if}
                            {/each}
                        </select>
                    </div>
                {/if}
                <div class="col-lg-5 mb-1">
                    <label for="team-select">Filter By Team</label>
                    <select
                        bind:value={selectedTeam}
                        on:change={() => {
                            players = players;
                        }}
                        class="form-select form-select-lg"
                        id="team-select"
                    >
                        <option value="all">All</option>
                        {#each teams as team}
                            <option value={team.short_name}
                                >{team.city} {team.mascot}</option
                            >
                        {/each}
                    </select>
                </div>
                {#if premium_membership}
                    <div class="col-lg-2 mb-1">
                        <label for="num-lineup">Lineups</label>
                        <select
                            bind:value={num_lineups}
                            id="num-lineup"
                            class="form-select form-select-lg"
                            aria-label=".form-select-lg example"
                        >
                            <option value="1">1</option>
                            <option value="3">3</option>
                            <option value="5">5</option>
                            <option value="10">10</option>
                            <option value="25">25</option>
                            <option value="50">50</option>
                            <option value="100">100</option>
                            <option value="150">150</option>
                        </select>
                        {#if parseInt(num_lineups) >= 50 && strategy == "projection" && exposure_strategy == "none"}
                            <div class="form-text">Big projection-only builds can take a few minutes. Randomization or an exposure limit in ⚙️ Advanced Settings finishes in a fraction of the time and spreads the lineups out.</div>
                        {/if}
                    </div>
                {:else}
                    <div
                        class="col-lg-2 mb-1"
                        on:click={() => {
                            showPremiumModal();
                        }}
                    >
                        <label for="num-lineup">Lineups</label>
                        <div style="cursor: not-allowed;">
                            <select
                                disabled
                                bind:value={num_lineups}
                                id="num-lineup"
                                class="form-select form-select-lg"
                                aria-label=".form-select-lg example"
                                style="pointer-events:none;"
                            >
                                <option value="1">1</option>
                                <option value="3">3</option>
                            </select>
                        </div>
                    </div>
                {/if}
            </div>
            <div>
                <ul class="nav nav-pills mb-3 h4" id="pills-tab" role="tablist">
                    <li class="nav-item" role="presentation">
                        <button
                            class="nav-link active"
                            id="pills-consider-tab"
                            data-bs-toggle="pill"
                            data-bs-target="#pills-consider"
                            type="button"
                            role="tab"
                            aria-controls="pills-consider"
                            aria-selected="true">Considering</button
                        >
                    </li>
                    <li class="nav-item" role="presentation">
                        <button
                            class="nav-link"
                            id="pills-exclude-tab"
                            data-bs-toggle="pill"
                            data-bs-target="#pills-exclude"
                            type="button"
                            role="tab"
                            aria-controls="pills-exclude"
                            aria-selected="false">Excluded</button
                        >
                    </li>
                    <div class="my-auto">
                        <button
                            type="button"
                            class="btn btn-lg btn-secondary"
                            data-bs-toggle="modal"
                            data-bs-target="#settings-modal"
                            >⚙️Advanced Settings</button
                        >
                        {#if premium_membership}
                            <button
                                type="button"
                                class="btn btn-lg btn-outline-secondary ms-1"
                                data-bs-toggle="modal"
                                data-bs-target="#entries-modal"
                                title="Upload your DraftKings or FanDuel entries file to fill it with lineups, or to late swap it once games have started"
                                >📂Entries File</button
                            >
                        {/if}
                    </div>
                </ul>
                <div class="row d-lg-none">
                    <div
                        class="col-12 mb-2 col-12 mb-2 d-flex align-items-center"
                    >
                        <span class="h6 me-auto">
                            ${salary.toLocaleString()} Rem | {projFP} proj | {players_rostered}
                            / {roster_size} Locked</span
                        ><button
                            class="btn btn-primary"
                            type="button"
                            data-bs-toggle="offcanvas"
                            data-bs-target="#offcanvasScrolling"
                            aria-controls="offcanvasScrolling"
                            >Show Roster</button
                        >
                    </div>
                </div>
                <div class="row pb-2">
                    <div class="col-xl-4 mb-1">
                        <div
                            class="d-flex align-items-center h-100 btn-group"
                            role="group"
                            aria-label="Basic radio toggle button group"
                        >
                            <input
                                type="radio"
                                bind:group={pos_filter}
                                on:click={() => (players = players)}
                                value="all"
                                class="btn-check"
                                name="btnradio"
                                id="btnradio1"
                                autocomplete="off"
                            />
                            <label
                                class="w-100 btn btn-outline-primary"
                                for="btnradio1">All</label
                            >
                            {#each position_filters as position, index}
                                <input
                                    type="radio"
                                    bind:group={pos_filter}
                                    on:click={() => (players = players)}
                                    class="btn-check"
                                    name="btnradio"
                                    id="btnradio{index + 2}"
                                    value={position}
                                    autocomplete="off"
                                />
                                <label
                                    class="w-100 btn btn-outline-primary"
                                    for="btnradio{index + 2}">{position}</label
                                >
                            {/each}
                        </div>
                    </div>
                    <div class="col-xl-4 mb-1">
                        <div class="input-group flex-nowrap input-group-lg">
                            <span
                                class="input-group-text"
                                id="inputGroup-sizing-lg">🔍</span
                            >
                            <input
                                on:keyup={() => {
                                    setTimeout((players = players), 500);
                                }}
                                bind:value={playerSearchString}
                                autocomplete="off"
                                id="player-search"
                                type="text"
                                class="form-control"
                                aria-label="Sizing example input"
                                placeholder="Search for a Player..."
                                aria-describedby="inputGroup-sizing-lg"
                            />
                        </div>
                    </div>
                    <div class="col-xl-4 mb-1">
                        <div class="d-flex">
                            <button
                                on:click={() => clearRoster()}
                                class="w-50 me-1 btn btn-lg btn-danger"
                            >
                                🚫Reset
                            </button>
                            <button
                                on:click={() => optimizeLineup()}
                                class="w-50 ms-1 btn btn-lg btn-primary" style="white-space: nowrap;"
                            >
                                📈Optimize
                            </button>
                        </div>
                    </div>
                </div>
                <div class="tab-content" id="pills-tabContent">
                    <div
                        class="tab-pane fade show active"
                        id="pills-consider"
                        role="tabpanel"
                        aria-labelledby="pills-consider-tab"
                    >
                        <div class="d-md-none">
                            <div class="">
                                {#each players as player, index}
                                    {#if canRosterAnywhere(player) && player.considered && !player.rostered && positionFilter(player, pos_filter) && searchFilter(player, playerSearchString) && filterByTeam(player.team, selectedTeam)}
                                        <PlayerCard
                                            {player}
                                            {showPlayerModal}
                                            {index}
                                            player_name={player.name}
                                            player_image={player.img_thumb}
                                            inj_tag={player.inj_tag}
                                            positions={player.positions}
                                            salary={player.salary}
                                            projection={player.proj}
                                            {rosterPlayer}
                                            {excludePlayer}
                                            included={true}
                                            {includePlayer}
                                            game_details={teamGameStrings[
                                                player.team
                                            ]}
                                        />
                                    {:else if player.considered && !player.rostered && positionFilter(player, pos_filter) && searchFilter(player, playerSearchString) && filterByTeam(player.team, selectedTeam)}
                                        <PlayerCard
                                            {player}
                                            {showPlayerModal}
                                            {index}
                                            player_name={player.name}
                                            player_image={player.img_thumb}
                                            inj_tag={player.inj_tag}
                                            positions={player.positions}
                                            salary={player.salary}
                                            projection={player.proj}
                                            {rosterPlayer}
                                            {excludePlayer}
                                            included={true}
                                            {includePlayer}
                                            rosterable="unrosterable"
                                            game_details={teamGameStrings[
                                                player.team
                                            ]}
                                        />
                                    {/if}
                                {/each}
                            </div>
                        </div>

                        <div
                            class="table-responsive card modern-card p-3 d-none d-md-block"
                            id="considered-players"
                        >
                            <table class="table">
                                <thead class="text-center">
                                    {#if sortableCols["name"] == 1}
                                        <th
                                            class="text-start sort-desc sorter pointer"
                                            on:click={() => sortByX("name")}
                                            >Player</th
                                        >
                                    {:else if sortableCols["name"] == -1}
                                        <th
                                            class="text-start sort-asc sorter pointer"
                                            on:click={() => sortByX("name")}
                                            >Player</th
                                        >
                                    {:else}
                                        <th
                                            draggable="true"
                                            class="text-start sorter pointer"
                                            on:click={() => sortByX("name")}
                                            >Player</th
                                        >
                                    {/if}
                                    {#if sortableCols["salary"] == 1}
                                        <th
                                            class="sort-desc sorter pointer"
                                            on:click={() => sortByX("salary")}
                                            >Salary</th
                                        >
                                    {:else if sortableCols["salary"] == -1}
                                        <th
                                            class="sort-asc sorter pointer"
                                            on:click={() => sortByX("salary")}
                                            >Salary</th
                                        >
                                    {:else}
                                        <th
                                            class="sorter pointer"
                                            on:click={() => sortByX("salary")}
                                            >Salary</th
                                        >
                                    {/if}
                                    {#if sortableCols["proj"] == 1}
                                        <th
                                            class="sort-desc sorter pointer"
                                            on:click={() => sortByX("proj")}
                                            >FP</th
                                        >
                                    {:else if sortableCols["proj"] == -1}
                                        <th
                                            class="sort-asc sorter pointer"
                                            on:click={() => sortByX("proj")}
                                            >FP</th
                                        >
                                    {:else}
                                        <th
                                            class="sorter pointer"
                                            on:click={() => sortByX("proj")}
                                            >FP</th
                                        >
                                    {/if}
                                    {#if sortableCols["val"] == 1}
                                        <th
                                            class="sort-desc sorter pointer"
                                            on:click={() => sortByValue()}
                                            >Value</th
                                        >
                                    {:else if sortableCols["val"] == -1}
                                        <th
                                            class="sort-asc sorter pointer"
                                            on:click={() => sortByValue()}
                                            >Value</th
                                        >
                                    {:else}
                                        <th
                                            class="sorter pointer"
                                            on:click={() => sortByValue()}
                                            >Value</th
                                        >
                                    {/if}
                                    <th>Lock</th><th>Exclude</th>
                                </thead>
                                <tbody>
                                    {#each players as player, index}
                                        {#if canRosterAnywhere(player) && player.considered && !player.rostered && positionFilter(player, pos_filter) && searchFilter(player, playerSearchString) && filterByTeam(player.team, selectedTeam)}
                                            <tr
                                                class="draggable"
                                                draggable="true"
                                                on:dragend={function (event) {
                                                    dragend(event);
                                                }}
                                                on:dragstart={(event) =>
                                                    drag(event, index)}
                                            >
                                                <td class="pb-0">
                                                    <div class="d-flex">
                                                        <img
                                                            class="mt-auto"
                                                            style="object-fit: contain;"
                                                            alt="{player.name}"
                                                            loading="lazy"
                                                            height="65"
                                                            width="89"
                                                            src={player.img_thumb}
                                                            onerror="this.src='https://dfstud.nyc3.digitaloceanspaces.com/dfstud/static/fantasy/img/brand/horse_default_profile2.png';"
                                                        />
                                                        <div
                                                            class="lead d-flex flex-column"
                                                        >
                                                            <span
                                                                class="pointer"
                                                                on:click={() => {
                                                                    showPlayerModal(
                                                                        player
                                                                    );
                                                                }}
                                                                ><span
                                                                    class="px-1 border bg-light"
                                                                    >{player.positions}</span
                                                                >
                                                                <span class="text-primary fw-normal">{player.name}</span></span
                                                            >
                                                            <span
                                                                >{@html teamGameStrings[
                                                                    player.team
                                                                ]}
                                                                {#if player.inj_tag == "O"}
                                                                    <span
                                                                        class="badge bg-danger"
                                                                        >OUT</span
                                                                    >
                                                                {:else if player.inj_tag == "Q" || player.inj_tag == "D" || player.inj_tag == "GTD"}
                                                                    <span
                                                                        class="badge bg-warning"
                                                                        >{player.inj_tag}</span
                                                                    >
                                                                {/if}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td class="text-success" style="white-space: nowrap;"
                                                    >💸{player.salary.toLocaleString()}</td
                                                >
                                                <td class="text-primary">
                                                    <div
                                                        style="width:fit-content;"
                                                        class="input-group flex-nowrap mx-auto"
                                                    >
                                                        <span
                                                            class="input-group-text"
                                                            >📊</span
                                                        >
                                                        <input
                                                            class="form-control"
                                                            style="width:75px;"
                                                            type="number"
                                                            bind:value={player.proj}
                                                        />
                                                    </div>
                                                </td>
                                                <td
                                                    >{Math.round(
                                                        (player.proj /
                                                            (player.salary /
                                                                1000)) *
                                                            100
                                                    ) / 100}</td
                                                ><td
                                                    class="pointer h5 text-center"
                                                    on:click={() =>
                                                        rosterPlayer(index)}
                                                    >🔒</td
                                                ><td
                                                    class="text-center h5 pointer"
                                                    on:click={() =>
                                                        excludePlayer(index)}
                                                    >❌</td
                                                ></tr
                                            >
                                        {:else if player.considered && !player.rostered && positionFilter(player, pos_filter) && searchFilter(player, playerSearchString) && filterByTeam(player.team, selectedTeam)}
                                            <tr class="unrosterable">
                                                <td class="pb-0">
                                                    <div class="d-flex">
                                                        <img
                                                            class="mt-auto"
                                                            style="object-fit: contain;"
                                                            alt="{player.name}"
                                                            loading="lazy"
                                                            height="65"
                                                            width="89"
                                                            src={player.img_thumb}
                                                            onerror="this.src='https://dfstud.nyc3.digitaloceanspaces.com/dfstud/static/fantasy/img/brand/horse_default_profile2.png';"
                                                        />
                                                        <div
                                                            class="lead d-flex flex-column"
                                                        >
                                                            <span
                                                                class="pointer"
                                                                on:click={() => {
                                                                    showPlayerModal(
                                                                        player
                                                                    );
                                                                }}
                                                                ><span
                                                                    class="px-1 border bg-light"
                                                                    >{player.positions}</span
                                                                >
                                                                <span class="text-primary fw-normal">{player.name}</span></span
                                                            >
                                                            <span
                                                                >{@html teamGameStrings[
                                                                    player.team
                                                                ]}
                                                                {#if player.inj_tag == "O"}
                                                                    <span
                                                                        class="badge bg-danger"
                                                                        >OUT</span
                                                                    >
                                                                {:else if player.inj_tag == "Q" || player.inj_tag == "D" || player.inj_tag == "GTD"}
                                                                    <span
                                                                        class="badge bg-warning"
                                                                        >{player.inj_tag}</span
                                                                    >
                                                                {/if}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td class="text-success" style="white-space: nowrap;"
                                                    >💸{player.salary.toLocaleString()}</td
                                                >
                                                <td class="text-primary">
                                                    <div
                                                        style="width:fit-content;"
                                                        class="input-group flex-nowrap mx-auto"
                                                    >
                                                        <span
                                                            class="input-group-text"
                                                            >📊</span
                                                        >
                                                        <input
                                                            class="form-control"
                                                            style="width:75px;"
                                                            type="number"
                                                            bind:value={player.proj}
                                                        />
                                                    </div>
                                                </td>
                                                <td
                                                    >{Math.round(
                                                        (player.proj /
                                                            (player.salary /
                                                                1000)) *
                                                            100
                                                    ) / 100}</td
                                                ><td
                                                    class="pointer text-center h5"
                                                    on:click={() =>
                                                        rosterPlayer(index)}
                                                    >🔒</td
                                                ><td
                                                    class="pointer h5 text-center"
                                                    on:click={() =>
                                                        excludePlayer(index)}
                                                    >❌</td
                                                ></tr
                                            >
                                        {/if}
                                    {/each}
                                </tbody>
                            </table>
                        </div>
                    </div>
                    <div
                        class="tab-pane fade"
                        id="pills-exclude"
                        role="tabpanel"
                        aria-labelledby="pills-exclude-tab"
                    >
                        <div class="d-md-none">
                            <div class="">
                                {#each players as player, index}
                                    {#if canRosterAnywhere(player) && !player.considered && !player.rostered && positionFilter(player, pos_filter) && searchFilter(player, playerSearchString) && filterByTeam(player.team, selectedTeam)}
                                        <PlayerCard
                                            {player}
                                            {showPlayerModal}
                                            {index}
                                            player_name={player.name}
                                            player_image={player.img_thumb}
                                            inj_tag={player.inj_tag}
                                            positions={player.positions}
                                            salary={player.salary}
                                            projection={player.proj}
                                            {rosterPlayer}
                                            {excludePlayer}
                                            included={false}
                                            {includePlayer}
                                            game_details={teamGameStrings[
                                                player.team
                                            ]}
                                        />
                                    {:else if !player.considered && !player.rostered && positionFilter(player, pos_filter) && searchFilter(player, playerSearchString) && filterByTeam(player.team, selectedTeam)}
                                        <PlayerCard
                                            {player}
                                            {showPlayerModal}
                                            {index}
                                            player_name={player.name}
                                            player_image={player.img_thumb}
                                            inj_tag={player.inj_tag}
                                            positions={player.positions}
                                            salary={player.salary}
                                            projection={player.proj}
                                            {rosterPlayer}
                                            {excludePlayer}
                                            included={false}
                                            {includePlayer}
                                            rosterable="unrosterable"
                                            game_details={teamGameStrings[
                                                player.team
                                            ]}
                                        />
                                    {/if}
                                {/each}
                            </div>
                        </div>
                        <div
                            class="table-responsive modern-card p-3 d-none d-md-block"
                            id="excluded-players"
                        >
                            <table class="table">
                                <thead class="text-center">
                                    {#if sortableCols["name"] == 1}
                                        <th
                                            class="text-start sort-desc sorter pointer"
                                            on:click={() => sortByX("name")}
                                            >Player</th
                                        >
                                    {:else if sortableCols["name"] == -1}
                                        <th
                                            class="text-start sort-asc sorter pointer"
                                            on:click={() => sortByX("name")}
                                            >Player</th
                                        >
                                    {:else}
                                        <th
                                            draggable="true"
                                            class="text-start sorter pointer"
                                            on:click={() => sortByX("name")}
                                            >Player</th
                                        >
                                    {/if}
                                    {#if sortableCols["salary"] == 1}
                                        <th
                                            class="sort-desc sorter pointer"
                                            on:click={() => sortByX("salary")}
                                            >Salary</th
                                        >
                                    {:else if sortableCols["salary"] == -1}
                                        <th
                                            class="sort-asc sorter pointer"
                                            on:click={() => sortByX("salary")}
                                            >Salary</th
                                        >
                                    {:else}
                                        <th
                                            class="sorter pointer"
                                            on:click={() => sortByX("salary")}
                                            >Salary</th
                                        >
                                    {/if}
                                    {#if sortableCols["proj"] == 1}
                                        <th
                                            class="sort-desc sorter pointer"
                                            on:click={() => sortByX("proj")}
                                            >FP</th
                                        >
                                    {:else if sortableCols["proj"] == -1}
                                        <th
                                            class="sort-asc sorter pointer"
                                            on:click={() => sortByX("proj")}
                                            >FP</th
                                        >
                                    {:else}
                                        <th
                                            class="sorter pointer"
                                            on:click={() => sortByX("proj")}
                                            >FP</th
                                        >
                                    {/if}
                                    {#if sortableCols["val"] == 1}
                                        <th
                                            class="sort-desc sorter pointer"
                                            on:click={() => sortByValue()}
                                            >Value</th
                                        >
                                    {:else if sortableCols["val"] == -1}
                                        <th
                                            class="sort-asc sorter pointer"
                                            on:click={() => sortByValue()}
                                            >Value</th
                                        >
                                    {:else}
                                        <th
                                            class="sorter pointer"
                                            on:click={() => sortByValue()}
                                            >Value</th
                                        >
                                    {/if}
                                    <th>Include</th>
                                </thead>
                                <tbody>
                                    {#each players as player, index}
                                        {#if canRosterAnywhere(player) && !player.considered && !player.rostered && positionFilter(player, pos_filter) && searchFilter(player, playerSearchString) && filterByTeam(player.team, selectedTeam)}
                                            <tr>
                                                <td class="pb-0">
                                                    <div class="d-flex">
                                                        <img
                                                            class="mt-auto"
                                                            style="object-fit: contain;"
                                                            alt="{player.name}"
                                                            loading="lazy"
                                                            height="65"
                                                            width="89"
                                                            src={player.img_thumb}
                                                            onerror="this.src='https://dfstud.nyc3.digitaloceanspaces.com/dfstud/static/fantasy/img/brand/horse_default_profile2.png';"
                                                        />
                                                        <div
                                                            class="lead d-flex flex-column"
                                                        >
                                                            <span
                                                                class="pointer"
                                                                on:click={() => {
                                                                    showPlayerModal(
                                                                        player
                                                                    );
                                                                }}
                                                                ><span
                                                                    class="px-1 border bg-light"
                                                                    >{player.positions}</span
                                                                >
                                                                <span class="text-primary fw-normal">{player.name}</span></span
                                                            >
                                                            <span
                                                                >{@html teamGameStrings[
                                                                    player.team
                                                                ]}
                                                                {#if player.inj_tag == "O"}
                                                                    <span
                                                                        class="badge bg-danger"
                                                                        >OUT</span
                                                                    >
                                                                {:else if player.inj_tag == "Q" || player.inj_tag == "D" || player.inj_tag == "GTD"}
                                                                    <span
                                                                        class="badge bg-warning"
                                                                        >{player.inj_tag}</span
                                                                    >
                                                                {/if}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td class="text-success" style="white-space: nowrap;"
                                                    >💸{player.salary.toLocaleString()}</td
                                                ><td class="text-primary"
                                                    >📊{player.proj}</td
                                                ><td
                                                    >{Math.round(
                                                        (player.proj /
                                                            (player.salary /
                                                                1000)) *
                                                            100
                                                    ) / 100}</td
                                                ><td
                                                    class="pointer text-center h5"
                                                    on:click={() =>
                                                        includePlayer(index)}
                                                    >✔️</td
                                                ></tr
                                            >
                                        {:else if !player.considered && !player.rostered && positionFilter(player, pos_filter) && searchFilter(player, playerSearchString) && filterByTeam(player.team, selectedTeam)}
                                            <tr class="unrosterable">
                                                <td class="pb-0">
                                                    <div class="d-flex">
                                                        <img
                                                            class="mt-auto"
                                                            height="65"
                                                            width="89"
                                                            loading="lazy"
                                                            alt="{player.name}"
                                                            src={player.img_thumb}
                                                            onerror="this.src='https://dfstud.nyc3.digitaloceanspaces.com/dfstud/static/fantasy/img/brand/horse_default_profile2.png';"
                                                        />
                                                        <div
                                                            class="lead d-flex flex-column"
                                                        >
                                                            <span
                                                                class="pointer"
                                                                on:click={() => {
                                                                    showPlayerModal(
                                                                        player
                                                                    );
                                                                }}
                                                                ><span
                                                                    class="px-1 border bg-light"
                                                                    >{player.positions}</span
                                                                >
                                                                <span class="text-primary fw-normal">{player.name}</span></span
                                                            >
                                                            <span
                                                                >{@html teamGameStrings[
                                                                    player.team
                                                                ]}
                                                                {#if player.inj_tag == "O"}
                                                                    <span
                                                                        class="badge bg-danger"
                                                                        >OUT</span
                                                                    >
                                                                {:else if player.inj_tag == "Q" || player.inj_tag == "D" || player.inj_tag == "GTD"}
                                                                    <span
                                                                        class="badge bg-warning"
                                                                        >{player.inj_tag}</span
                                                                    >
                                                                {/if}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td class="text-success" style="white-space: nowrap;"
                                                    >💸{player.salary.toLocaleString()}</td
                                                ><td class="text-primary"
                                                    >📊{player.proj}</td
                                                ><td
                                                    >{Math.round(
                                                        (player.proj /
                                                            (player.salary /
                                                                1000)) *
                                                            100
                                                    ) / 100}</td
                                                ><td
                                                    class="pointer text-center h5"
                                                    on:click={() =>
                                                        includePlayer(index)}
                                                    >✔️</td
                                                ></tr
                                            >
                                        {/if}
                                    {/each}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div class="col-lg-4 d-none d-lg-block">
            <div class="roster-holder card modern-card">
                <div class="px-3 d-flex h5 bold text-center border-bottom py-2">
                    <div id="salary-summary" class="px-3">
                        <div>Salary</div>
                        <div>${salary.toLocaleString()}</div>
                    </div>
                    <div id="fp-summary" class="px-3">
                        <div>Projection</div>
                        <div>{projFP} FP</div>
                    </div>
                </div>
                <div class="d-flex flex-column">
                    {#each Object.entries(roster) as [pos, slot]}
                        {#if slot.player}
                            <div
                                style="border-radius: 10px;"
                                class="px-3 hidden-border border-bottom roster-slot d-flex align-items-center"
                            >
                                <div class="display-5 col-4">
                                    <img
                                        alt="{slot.player.name}"
                                        height="70"
                                        src={slot.player.img_thumb}
                                        loading="lazy"
                                        onerror="this.src='https://dfstud.nyc3.digitaloceanspaces.com/dfstud/static/fantasy/img/brand/horse_default_profile2.png';"
                                    />
                                </div>
                                <div
                                    class="col-8 h6 my-0 d-flex align-items-center"
                                >
                                    <div class="d-flex flex-column">
                                        <div>
                                            {slot.player.name}
                                            {#if slot.player.inj_tag == "O"}
                                                <span class="badge bg-danger"
                                                    >OUT</span
                                                >
                                            {:else if slot.player.inj_tag == "Q" || slot.player.inj_tag == "D" || slot.player.inj_tag == "GTD"}
                                                <span class="badge bg-warning"
                                                    >{slot.player.inj_tag}</span
                                                >
                                            {/if}
                                        </div>
                                        <div>
                                            <span class="text-success" style="white-space: nowrap;"
                                                >💸{displayedSlotSalary(pos).toLocaleString()}</span
                                            >
                                            |
                                            <span class="text-primary"
                                                >📊{slot.player.proj}</span
                                            >
                                        </div>
                                    </div>
                                    <span
                                        class="ms-auto h5 pointer"
                                        on:click={() => unrosterPlayer(pos)}
                                        >🔓</span
                                    >
                                </div>
                            </div>
                        {:else}
                            <div
                                style="border-radius: 10px;"
                                on:drop={drop(pos)}
                                on:dragover={function (event) {
                                    allowDrop(event, pos);
                                }}
                                on:dragleave={function (event) {
                                    handleLeave(event);
                                }}
                                class="hidden-border roster-slot {slot.fillable_by
                                    .replaceAll(',', ' ')
                                    .replaceAll('[', '')
                                    .replaceAll(']', '')
                                    .replaceAll("'", '')
                                    .replaceAll(
                                        '"',
                                        ''
                                    )} {pos} d-flex align-items-center py-2 px-3"
                            >
                                <div class="no-pointer-events display-5 col-4">
                                    {slot.display_name}
                                </div>
                                <div
                                    class="no-pointer-events col-8 h6 my-0 d-flex align-items-center"
                                >
                                    Choose a Player
                                </div>
                            </div>
                        {/if}
                    {/each}
                </div>
            </div>
        </div>
        <div
            class="offcanvas offcanvas-end d-lg-none"
            data-bs-scroll="true"
            data-bs-backdrop="false"
            tabindex="-1"
            id="offcanvasScrolling"
            aria-labelledby="offcanvasScrollingLabel"
        >
            <div class="offcanvas-header">
                <div class="d-flex h5 bold text-center border-bottom py-2">
                    <div id="salary-summary" class="px-3">
                        <div>Salary</div>
                        <div>${salary.toLocaleString()}</div>
                    </div>
                    <div id="fp-summary" class="px-3">
                        <div>Projection</div>
                        <div>{projFP} FP</div>
                    </div>
                </div>
                <button
                    type="button"
                    class="btn-close text-reset"
                    data-bs-dismiss="offcanvas"
                    aria-label="Close"
                />
            </div>
            <div class="offcanvas-body">
                <div class="roster-holder">
                    <div class="d-flex flex-column">
                        {#each Object.entries(roster) as [pos, slot]}
                            {#if slot.player}
                                <div
                                    class="hidden-border border-bottom roster-slot d-flex align-items-center"
                                >
                                    <div class="display-5 col-4">
                                        <img
                                            alt="{slot.player.name}"
                                            height="70"
                                            src={slot.player.img_thumb}
                                            loading="lazy"
                                            onerror="this.src='https://dfstud.nyc3.digitaloceanspaces.com/dfstud/static/fantasy/img/brand/horse_default_profile2.png';"
                                        />
                                    </div>
                                    <div
                                        class="col-8 h6 my-0 d-flex align-items-center"
                                    >
                                        <div class="d-flex flex-column">
                                            <div>
                                                {slot.player.name}
                                                <div>
                                                    {#if slot.player.inj_tag == "O"}
                                                        <span
                                                            class="badge bg-danger"
                                                            >OUT</span
                                                        >
                                                    {:else if slot.player.inj_tag == "Q" || slot.player.inj_tag == "D" || slot.player.inj_tag == "GTD"}
                                                        <span
                                                            class="badge bg-warning"
                                                            >{slot.player
                                                                .inj_tag}</span
                                                        >
                                                    {/if}
                                                </div>
                                            </div>
                                            <div>
                                                <span class="text-success" style="white-space: nowrap;"
                                                    >💸{displayedSlotSalary(pos).toLocaleString()}</span
                                                >
                                                |
                                                <span class="text-primary"
                                                    >📊{slot.player.proj}</span
                                                >
                                            </div>
                                        </div>
                                        <span
                                            class="ms-auto h5 pointer"
                                            on:click={() => unrosterPlayer(pos)}
                                            >🔓</span
                                        >
                                    </div>
                                </div>
                            {:else}
                                <div
                                    on:drop={drop(pos)}
                                    on:dragover={function (event) {
                                        allowDrop(event, pos);
                                    }}
                                    on:dragleave={function (event) {
                                        handleLeave(event);
                                    }}
                                    class="hidden-border roster-slot {slot.fillable_by
                                        .replaceAll(',', ' ')
                                        .replaceAll('[', '')
                                        .replaceAll(']', '')
                                        .replaceAll("'", '')
                                        .replaceAll(
                                            '"',
                                            ''
                                        )} {pos} d-flex align-items-center py-2"
                                >
                                    <div class="no-pointer-events display-5 col-4">
                                        {slot.display_name}
                                    </div>
                                    <div
                                        class="no-pointer-events col-8 h6 my-0 d-flex align-items-center"
                                    >
                                        Choose a Player
                                    </div>
                                </div>
                            {/if}
                        {/each}
                    </div>
                </div>
            </div>
        </div>
    </div>
</div>

<style>
    td {
        vertical-align: bottom;
    }
    .hidden-border {
        border: 1px solid white;
        border-collapse: collapse;
    }
    .over {
        background-color: #f5ee9e;
    }
    .highlight {
        border: 1px dotted black;
        border-collapse: collapse;
        border-radius: 5px;
    }
    .hide {
        transform: translateX(-9999px);
    }
    .unrosterable {
        opacity: 0.5;
    }
    .pointer {
        cursor: pointer;
    }
    .sorter:after,
    .sorter:before {
        position: absolute;
        bottom: 0em;
        display: block;
        opacity: 0.3;
        height: 100%;
    }
    .sorter:after {
        right: 0.5em;
        content: "↓";
    }
    .sorter:before {
        right: 1em;
        content: "↑";
    }
    .sorter {
        padding-right: 30px;
        position: relative;
    }
    .sorter.sort-asc:before {
        opacity: 1 !important;
    }
    .sorter.sort-desc:after {
        opacity: 1 !important;
    }
    .draggable {
        cursor: grab;
    }
    .roster-holder {
        position: sticky;
        top: 65px;
    }
    .roster-slot {
        max-height: 75px;
    }
    .padding-r-30 {
        padding-right: 30px;
    }
    .invisible_div {
        width: -webkit-fill-available;
        height: 55px;
        background: rgba(255, 0, 0, 0);
        margin-top: -50px;
        z-index: 1;
        position: absolute;
    }
    .no-pointer-events {
        pointer-events: none;
    }
</style>
